import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLinkWithHref } from '@angular/router';
import type { DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import type { PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { DeadlineTypeEnum } from '@zaoblako/shared/common/enums/deadline.type.enum';
import { TemplateEntityPeriodicityEnum } from '@zaoblako/shared/common/enums/template.entity.periodicity.enum';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import { ApprovalSchemeQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-query.dto';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskFindQueryInterface } from '@zaoblako/shared/modules/task/dtos/task-find-query.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import type { TaskTemplateFindInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-find.dto';
import type { TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task-find.dto';
import {
  TaskTemplateFormDto,
  type TaskTemplateFormInterface
} from '@zaoblako/shared/modules/task/dtos/template/task-template-form.dto';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { TaskStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/task.status.type.enum';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ApprovalSchemeService } from '@zaoblako/web/core/services/budget/approval-scheme.service';
import { BankService } from '@zaoblako/web/core/services/catalog/bank.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { FilialService } from '@zaoblako/web/core/services/finance/filial.service';
import { NdsService } from '@zaoblako/web/core/services/finance/nds.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { QueryParamsService } from '@zaoblako/web/core/services/query-params.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { TaskPriorityService } from '@zaoblako/web/core/services/task/task-priority.service';
import { TaskStatusService } from '@zaoblako/web/core/services/task/task-status.service';
import { TaskTemplateService } from '@zaoblako/web/core/services/task/task-template.service';
import { TaskService } from '@zaoblako/web/core/services/task/task.service';
import { CardContragentInfoComponent } from '@zaoblako/web/modules/payments/dumbs/card-contragent-info/card-contragent-info.component';
import { TaskTemplateHistoryWidgetComponent } from '@zaoblako/web/modules/payments/dumbs/task-template-history-widget/task-template-history-widget.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ApprovalSchemeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/approval-scheme-single-select/approval-scheme-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { PlanningDeadlineFormComponent } from '@zaoblako/web/shared/dumbs/planning-deadline-form/planning-deadline-form.component';
import { PlanningPeriodicityCardComponent } from '@zaoblako/web/shared/dumbs/planning-periodicity-card/planning-periodicity-card.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { TemplateSubjectInputComponent } from '@zaoblako/web/shared/dumbs/template-subject-input/template-subject-input.component';
import { UserCardPreviewComponent } from '@zaoblako/web/shared/dumbs/user-card-preview/user-card-preview.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { PaymentTypePipe } from '@zaoblako/web/shared/pipes/payment-type.pipe';
import { TaskContragentFormInterface } from '@zaoblako/shared/modules/task/dtos/contragent/task-contragent-form.dto';
import moment from 'moment-timezone';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable, Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-task-template-form',
  templateUrl: 'task-template-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    CheckboxModule,
    FormsModule,
    CardContragentInfoComponent,
    SingleSelectComponent,
    DirectionSingleSelectComponent,
    PaymentTypePipe,
    TemplateSubjectInputComponent,
    InputNumberModule,
    PlanningDeadlineFormComponent,
    PlanningPeriodicityCardComponent,
    DividerModule,
    MultiSelectComponent,
    TaskTemplateHistoryWidgetComponent,
    ButtonModule,
    SpinnerComponent,
    DealAutoCompleteComponent,
    UserCardPreviewComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    ApprovalSchemeSingleSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    CostCenterSingleSelectComponent,
    TableComponent,
    RouterLinkWithHref,
    DatepickerComponent,
    CurrencySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskTemplateFormComponent
  extends BaseFormComponent<TaskTemplateFormInterface>
  implements OnInit, OnDestroy
{
  @Input() public readonly index: number;
  public taskTemplate = <TaskTemplateFormInterface>{};
  public dto = TaskTemplateFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public companies: CompanyFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public priorities: TaskPriorityFindInterface[] = [];
  public nds: NdsFindInterface[] = [];
  public banks: BankFindInterface[] = [];
  public users: UserFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public schemes: ApprovalSchemeFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];
  public statuses: TaskStatusFindInterface[] = [];
  public filials: FilialFindInterface[] = [];

  public engine: 'class-transformer' | 'serde' = 'serde';

  public loadingTasks = true;
  public tasks: TaskFindInterface[] = [];
  public readonly taskColumns: CrmTableColumn[] = [{ label: 'Номер' }, { label: 'Дата' }];

  public readonly types = Object.values(TaskPaymentTypeEnum);
  public today = moment().format('YYYY-MM-DD');

  private querySubscription$: Subscription;

  public get contragent(): TaskContragentFormInterface {
    return this.taskTemplate.contragent;
  }

  public set contragent(value: TaskContragentFormInterface) {
    this.taskTemplate.contragent = value as TaskTemplateFormInterface['contragent'];
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly taskService: TaskService,
    private readonly taskTemplateService: TaskTemplateService,
    private readonly confirmService: ConfirmDialogService,
    private readonly companyService: CompanyService,
    private readonly filialService: FilialService,
    private readonly directionService: DirectionService,
    private readonly taskPriorityService: TaskPriorityService,
    private readonly ndsService: NdsService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly bankService: BankService,
    private readonly approvalSchemeService: ApprovalSchemeService,
    private readonly taskStatusService: TaskStatusService,
    private readonly errorService: ErrorService,
    public readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly queryParamsService: QueryParamsService
  ) {
    super();
  }

  public ngOnInit() {
    const taskId = this.formStoreService.getState.forms[this.index]?.data?.taskId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!isNaN(taskId)) {
      this.taskService.getTemplateFromTask(taskId).subscribe({
        next: (template) => {
          this.taskTemplate = template;
          this.cdRef.markForCheck();
        }
      });
    }

    forkJoin([
      this.taskStatusService.find<TaskStatusFindInterface>(),
      this.companyService.find<CompanyFindInterface>(),
      this.filialService.find<FilialFindInterface>(),
      this.directionService.find<DirectionFindInterface>({
        kinds: [
          DirectionKindEnum.OPERATING_EXPENSE,
          DirectionKindEnum.OTHER_EXPENSE,
          DirectionKindEnum.COMMERCIAL_EXPENSE,
          DirectionKindEnum.FINANCIAL_ACTIVITY,
          DirectionKindEnum.TECHNICAL_OPERATIONS
        ]
      }),
      this.taskPriorityService.find<TaskPriorityFindInterface>(),
      this.approvalSchemeService.find<ApprovalSchemeFindInterface>(<ApprovalSchemeQueryInterface>{
        onlyWithExplicitAccess: true
      })
    ]).subscribe(([statuses, companies, filials, directions, priorities, schemes]) => {
      this.statuses = statuses.filter((status) =>
        [TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL].includes(status.type)
      );
      this.companies = companies;
      this.filials = filials;
      this.directions = directions;
      this.priorities = priorities;
      this.schemes = schemes;

      if (this.taskTemplate.filial) {
        this.taskTemplate.filial = this.filials?.find((filial) => filial?.id === this.taskTemplate.filial?.id);
      }

      if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
        if (!this.taskTemplate.scheme) {
          this.taskTemplate.scheme = this.schemes?.find((scheme) => scheme.isDefault);
          this.changeUsersAndGroups(this.taskTemplate.scheme);
        }
        if (!this.taskTemplate.status) {
          this.taskTemplate.status = this.statuses?.find((status) => status.type === TaskStatusTypeEnum.SCHEDULED);
        }
        this.taskTemplate = { ...this.taskTemplate };
      }
      this.cdRef.markForCheck();
    });

    forkJoin([
      this.ndsService.find<NdsFindInterface>(),
      this.userService.findAll(),
      this.userGroupService.find<UserGroupFindInterface>()
    ]).subscribe(([nds, users, userGroups]) => {
      this.nds = nds;
      this.users = users;
      this.userGroups = userGroups;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.taskTemplate.planning = <PlanningUnitFormInterface>{};
      this.taskTemplate.planning.periodicity = TemplateEntityPeriodicityEnum.NONE;

      this.taskTemplate.deadline = <DeadlineFormInterface>{};
      this.taskTemplate.deadline.daysAfter = 0;
      this.taskTemplate.deadline.type = DeadlineTypeEnum.RELATIVE;
    }
  }

  public ngOnDestroy(): void {
    this.querySubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    forkJoin([
      this.taskTemplateService.findById<TaskTemplateFindInterface>(
        this.formStoreService.getState.forms[this.index]?.entityId
      ),
      this.approvalSchemeService.find<ApprovalSchemeFindInterface>()
    ]).subscribe(([taskTemplate, schemes]) => {
      this.taskTemplate = taskTemplate;
      this.taskTemplate.scheme = schemes?.find((scheme) => this.taskTemplate.scheme?.id === scheme?.id);
      this.formStoreService.setLoading(false, this.index);
      this.cdRef.markForCheck();

      this.taskService
        .findMany({ templateIds: [this.formStoreService.getState.forms[this.index]?.entityId], ignorePeriod: true })
        .subscribe({
          next: (tasks) => {
            this.tasks = tasks.items;
            this.loadingTasks = false;
            this.cdRef.markForCheck();
          }
        });
    });
  }

  public saveTemplate() {
    const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
    if (
      (requiredAnalyticalFeatures?.directionsRequired && !this.taskTemplate.direction) ||
      (requiredAnalyticalFeatures?.unitsRequired && !this.taskTemplate.unit) ||
      (requiredAnalyticalFeatures?.filialsRequired && !this.taskTemplate.filial)
    ) {
      this.errors = Object.create({});

      if (requiredAnalyticalFeatures?.directionsRequired && !this.taskTemplate.direction) {
        this.errors.direction = 'Бюджетная статья не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.unitsRequired && !this.taskTemplate.unit) {
        this.errors.unit = 'Бизнес единица не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.filialsRequired && !this.taskTemplate.filial) {
        this.errors.filial = 'Филиал не выбран' as any;
      }
      return;
    }

    if (this.checkValidate(this.taskTemplate).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.taskTemplateService
          .update<TaskTemplateFormInterface, TaskTemplateFindInterface>(this.taskTemplate)
          .subscribe({
            next: (template) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон "${template.name}" обновлен`);
              this.formStoreService.saveNext(template, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.taskTemplateService.create<TaskTemplateFindInterface>(this.taskTemplate).subscribe({
          next: (template) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон создан');
            this.formStoreService.saveNext(template, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTemplate() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон "${this.taskTemplate.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.taskTemplateService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Шаблон "${this.taskTemplate.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public searchBank(name: string) {
    this.bankService.search({ search: name }).subscribe((banks) => {
      this.banks = banks;
      this.cdRef.markForCheck();
    });
  }

  public changeUsersAndGroups(scheme: ApprovalSchemeFindInterface) {
    this.taskTemplate.userAuditors = [];
    this.taskTemplate.userGroupAuditors = [];
    if (scheme?.userAuditors?.length) {
      this.taskTemplate.userAuditors = scheme.userAuditors;
    }
    if (scheme?.userGroupAuditors?.length) {
      this.taskTemplate.userGroupAuditors = scheme.userGroupAuditors;
    }
  }

  public toTask(task: any): TaskFindInterface {
    return task as TaskFindInterface;
  }

  public directToTaskList() {
    const queryParams: TaskFindQueryInterface = {
      templateIds: [this.formStoreService.getState.forms[this.index]?.entityId],
      ignorePeriod: true
    };

    this.querySubscription$ = this.queryParamsService
      .getUrlTreeWithDigest(['/payments/task'], queryParams, true)
      .subscribe({
        next: (urlTree) => {
          const url = String(urlTree);
          window.open(url, '_blank');
        },
        error: (err) => {
          console.error('Failed to generate URL with digest:', err);
          this.errorService.addCustomError('Ошибка', 'Произошла ошибка, вернитесь на главную и попробуйте снова.');
        }
      });
  }

  public changeOrganization() {
    this.currencies = [];
    this.taskTemplate.currency = null;

    if (this.taskTemplate.organization?.currencies?.length) {
      this.currencies = this.taskTemplate.organization.currencies;
      setTimeout(() => {
        this.taskTemplate.currency = this.currencies.find((currency) => currency.isDefault);
        this.cdRef.markForCheck();
      });
    }
  }
}
